<template>
  <modal
    :name="name"
    :max-width="maxWidth"
    :adaptive="adaptive"
    :height="height"
    :width="width"
    :classes="classes"
    :click-to-close="clickToClose"
    @before-open="onBeforeOpen"
    @opened="onOpened"
    @before-close="onBeforeClose"
    @closed="onClosed"
  >
    <div class="modal_container" :class="containerClass">
      <div class="modal_header" v-if="$slots.header || showCloseButton || title">
        <slot name="header">
          <h2 v-if="title">{{ title }}</h2>
        </slot>
        <div v-if="showCloseButton" class="closer" @click="hide">
          <slot name="close-icon">
            <i class="bi bi-x-lg"></i>
          </slot>
        </div>
      </div>
      <div class="modal_body">
        <slot></slot>
      </div>
      <div class="modal_footer" v-if="$slots.footer">
        <slot name="footer"></slot>
      </div>
    </div>
  </modal>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { useModalStore } from '@/stores/ModalStore'
import { reportError } from '@lib/errorHandler'

/**
 * Simplified modal component that abstracts vue-js-modal
 *
 * This component provides a minimal abstraction over vue-js-modal with only
 * the most essential props. This will make it easier to replace with a
 * Bootstrap implementation in the future.
 *
 * It also integrates with the ModalStore for centralized modal management.
 */
export default defineComponent({
  name: 'UeModal',
  props: {
    // Modal identifier - required
    name: {
      type: String,
      required: true,
    },
    // Content
    title: {
      type: String,
      default: null,
    },
    // Show the close button in the header
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    // Additional class for the container
    containerClass: {
      type: String,
      default: '',
    },
    height: {
      type: String,
      default: 'auto',
    },
    width: {
      type: String,
      default: '600px', // default vue-js-modal width
    },
    maxWidth: {
      type: Number,
      default: Infinity,
    },
    // Basic behavior props
    adaptive: {
      type: Boolean,
      default: true,
    },
    classes: {
      type: [String, Array],
      default: '',
    },
    clickToClose: {
      type: Boolean,
      default: true,
    },
    /**
     * An optional function to override the close behavior.
     * This function receives a `close` callback as its only argument. Call `close()` to proceed with closing the modal.
     * You can perform asynchronous or synchronous logic before calling `close()` (e.g., show a confirmation dialog).
     * @param {Function} close - Call this function to close the modal.
     */
    closeOverride: {
      type: Function as PropType<(close: () => void) => void>,
    },
  },
  methods: {
    // Public methods that match vue-js-modal
    show(params = {}) {
      this.$modal.show(this.name, params)
    },
    hide() {
      try {
        const modalStore = useModalStore()
        const closer = (): void => {
          modalStore.close(this.name)
        }

        if (this.closeOverride) {
          this.closeOverride(closer)
        } else {
          closer()
        }
      } catch (e) {
        reportError(e, 'subscribe', 'UeModal')
      }
    },
    toggle() {
      this.$modal.toggle(this.name)
    },
    // Event handlers
    onBeforeOpen(event) {
      this.$emit('before-open', event)
    },
    onOpened(event) {
      this.$emit('opened', event)
    },
    onBeforeClose(event) {
      this.$emit('before-close', event)
    },
    onClosed(event) {
      this.$emit('closed', event)

      // Ensure the ModalStore is updated when the modal is closed by any means
      try {
        const modalStore = useModalStore()
        modalStore.close(this.name)
      } catch (e) {
        reportError(e, 'notify', 'UeModal')
      }
    },
  },
  mounted() {
    // Listen for changes in the ModalStore to automatically show or hide this modal
    try {
      const modalStore = useModalStore()

      const unwatch = modalStore.$subscribe((mutation, state) => {
        if (state.openModalNames.includes(this.name)) {
          const params = state.modalParams[this.name] || {}
          this.show(params)
        } else {
          this.$modal.hide(this.name)
        }
      })

      this.$options._modalStoreUnwatch = unwatch
    } catch (e) {
      reportError(e, 'subscribe', 'UeModal')
    }
  },
  beforeDestroy() {
    // Clean up the store subscription
    if (this.$options._modalStoreUnwatch) {
      this.$options._modalStoreUnwatch()
    }
  },
})
</script>

<style lang="sass" scoped>
.modal_container
  display: flex
  flex-direction: column
  height: 100%
  max-height: 100vh

.modal_body
  flex: 1 1 auto // this pushes the footer to the bottom of the modal despite the height of the modal body
  overflow-y: auto
  min-height: 0 // This is important for Firefox
</style>
